import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { formatTimestampMDY, formatDateOnlyMDY } from "../../utils/date";
import { APIResponse } from "../../types/selectUBT.types";
import { fetchLocations } from "../../services/auth/ubtService";
import {
  addDefaultOption,
  transformCostCenterOptions,
  transformToDropdownOptions,
} from "../../utils/dropdownTransformers";
import { DropdownOption } from "../../components/common/Dropdown";

// Types
interface ThunkError {
  message?: string;
  response?: {
    data?: {
      message?: string;
      error?: string;
      detail?: string;
    };
    statusText?: string;
  };
  code?: string;
}

interface TeamPayload {
  teamId?: number | string;
  id?: number | string;
  teamName?: string;
  ubtName?: string;
  teamDateCreated?: string;
  teamRating?: string;
  serviceDeliveryType?: string;
  serviceType?: string;
  usingHuddles?: string;
  defaultMembership?: string;
}

// Helper function to check if request was cancelled
const isRequestCancelled = (error: ThunkError): boolean => {
  return (
    error?.message === "canceled" ||
    error?.message === "Request cancelled" ||
    error?.code === "ECONNABORTED"
  );
};

export const defaultOption: DropdownOption = {
  id: 0,
  label: "Select",
  value: "Select",
};

// Helper function to extract error message from API response
const extractErrorMessage = (error: ThunkError, defaultMessage: string): string => {
  return (
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.response?.data?.detail ??
    error?.response?.statusText ??
    error?.message ??
    defaultMessage
  );
};

// Helper function for standard error handling in thunks
const handleThunkError = (error: ThunkError, defaultMessage: string): string => {
  if (isRequestCancelled(error)) {
    return "Request cancelled";
  }
  return extractErrorMessage(error, defaultMessage);
};

// Helper function to get standard request headers
const getStandardHeaders = (includeContentType = true): Record<string, string> => {
  const headers: Record<string, string> = {
    accept: "application/json",
    "x-nuid": localStorage.getItem("userId") ?? "V687124",
  };
  if (includeContentType) {
    headers["content-type"] = "application/json";
  }
  return headers;
};

// Helper function to map team details to state format
const mapTeamDetailsToState = (data: TeamPayload) => ({
  teamId: String(data.teamId ?? data.id ?? ""),
  ubtName: (data.teamName ?? data.ubtName ?? "").trim(),
  dateTeamEstablished: formatDateOnlyMDY(data.teamDateCreated),
  teamRating: data.teamRating,
  serviceType: data.serviceDeliveryType ?? data.serviceType,
  usingHuddles: data.usingHuddles === "Y" ? ("yes" as const) : ("no" as const),
  defaultMembership: data.defaultMembership === "Y" ? ("yes" as const) : ("no" as const),
});

// Helper function to map cost center item to CostCenter format
const mapCostCenterItem = (item: any): CostCenter => ({
  regionId: item.regionId ?? item.region_id ?? 0,
  glString: item.glString ?? item.gl_string ?? item.gl ?? "",
  region: item.region ?? item.regionName ?? item.kpCompanyDescr ?? item.kpGlRegionCodeDescr ?? "",
  regionCode: item.regionCode ?? item.kpGlRegionCd ?? "",
  entity: item.entity ?? item.entityName ?? "",
  entityCode: item.entityCode ?? item.company ?? "",
  entityId: item.entityId ?? item.entity_id ?? 0,
  area: item.area ?? item.areaName ?? item.kpAreaDesc ?? "",
  areaCode: item.areaCode ?? item.kpAreaCd ?? "",
  areaId: item.areaId ?? item.area_id ?? 0,
  center: item.center ?? item.centerName ?? item.kpSvcAreaDesc ?? "",
  centerCode: item.centerCode ?? item.kpSvcArea ?? "",
  centerId: item.centerId ?? item.center_id ?? 0,
  location: item.location ?? item.locationName ?? item.glLocNm ?? "",
  locationCode: item.locationCode ?? item.glLocCd ?? "",
  locationId: item.locationId ?? item.location_id ?? 0,
  costCenter: item.costCenter ?? item.cost_center ?? item.costCenterName ?? "",
  shift: item.shift ?? item.shiftName ?? "All",
  shiftId: item.shiftId ?? item.shift_id ?? 0,
  noOfEmployees: String(
    item.employeeCount ?? item.noOfEmployees ?? item.no_of_employees ?? "0"
  ),
  costCenterId: item.costCenterId ?? item.cost_center_id ?? item.deptPk ?? item.id ?? 0,
});

export interface CostCenter {
  glString: string;
  regionId: number;
  entityId: number;
  areaId: number;
  centerId: number;
  locationId: number;
  costCenterId: number;
  shiftId: number;
  region: string;
  regionCode?: string;
  entity: string;
  entityCode?: string;
  area: string;
  areaCode?: string;
  center: string;
  centerCode?: string;
  location: string;
  locationCode?: string;
  costCenter: string;
  shift: string;
  noOfEmployees: string;
}

const costCenters: CostCenterList[] = [];

interface CostCenterList {
  [key: string]: any;
}

interface CostCenterState {
  costCenters: CostCenterList[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  teamDetails: Record<string, any> | null;
  // Team profile data
  teamId: string;
  ubtName: string;
  dateTeamEstablished: string;
  teamRating: string;
  serviceType: string;
  usingHuddles: string;
  defaultMembership: string;
  // Cost center management
  costCenterList: CostCenter[];
  editableTeam: boolean;
  editingTeam: boolean;
  createTeam: boolean;
  // Team rating options
  teamRatingOptions: any[];
  loadingTeamRatingOptions: boolean;
  // Service type options
  serviceTypeOptions: any[];
  loadingServiceTypeOptions: boolean;
  // Shift options
  shiftOptions: any[];
  loadingShiftOptions: boolean;
  entityOptions: DropdownOption[];
  areaOptions: DropdownOption[];
  centerOptions: DropdownOption[];
  locationOptions: DropdownOption[];
  costCenterOptions: DropdownOption[];
  loadingDropdowns: boolean;
}

const initialState: CostCenterState = {
  costCenters: costCenters,
  loading: false,
  error: null,
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  totalPages: 0,
  teamDetails: null,
  // Team profile data
  teamId: "",
  ubtName: "",
  dateTeamEstablished: formatTimestampMDY(new Date()),
  teamRating: "1 Pre-Team Climate",
  serviceType: "Call Center",
  usingHuddles: "no",
  defaultMembership: "yes",
  // Cost center management
  costCenterList: [],
  editableTeam: false,
  editingTeam: false,
  createTeam: true,
  // Team rating options
  teamRatingOptions: [],
  loadingTeamRatingOptions: false,
  // Service type options
  serviceTypeOptions: [],
  loadingServiceTypeOptions: false,
  // Shift options
  shiftOptions: [],
  loadingShiftOptions: false,
  entityOptions: [],
  areaOptions: [],
  centerOptions: [],
  locationOptions: [],
  costCenterOptions: [defaultOption],
  loadingDropdowns: false,
};

export const fetchCostCenterUBTList = createAsyncThunk(
  "ubtPortal/fetchCostCenterUBTList",
  async (
    { page, perPage }: { page: number; perPage: number },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await api.get(
        endpoints.ubt.costCenter.costCenterUBTList,
        {
          signal,
          params: { page, per_page: perPage },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch history UBT List"),
      );
    }
  },
);

export const fetchTeamRatingOptions = createAsyncThunk(
  "costCenter/fetchTeamRatingOptions",
  async (_, { rejectWithValue, signal }) => {
    try {
      const response = await api.get(
        endpoints.ubt.costCenter.teamRatingOptions,
        {
          signal,
          headers: getStandardHeaders(false),
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch team rating options"),
      );
    }
  },
);

export const fetchServiceTypeOptions = createAsyncThunk(
  "costCenter/fetchServiceTypeOptions",
  async (_, { rejectWithValue, signal }) => {
    try {
      const response = await api.get(
        endpoints.ubt.costCenter.serviceTypeOptions,
        {
          signal,
          headers: getStandardHeaders(false),
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch service type options"),
      );
    }
  },
);

const SHIFT_OPTIONS = [
  { label: "All", value: "All" },
  { label: "4", value: "4" },
  { label: "W", value: "W" },
  { label: "Compressed", value: "Compressed" },
  { label: "Not Applicable", value: "Not Applicable" },
  { label: "Rotating", value: "Rotating" },
  { label: "Day", value: "Day" },
  { label: "Evening", value: "Evening" },
  { label: "Night", value: "Night" },
  { label: "No shift", value: "No shift" },
  { label: "Any", value: "Any" },
];

export const fetchShiftOptions = createAsyncThunk(
  "costCenter/fetchShiftOptions",
  async (_, { rejectWithValue }) => {
    try {
      return SHIFT_OPTIONS;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch shift options"),
      );
    }
  },
);

export const createUBTTeam = createAsyncThunk(
  "costCenter/createUBTTeam",
  async (payload: any, { rejectWithValue, signal }) => {
    try {
      const response = await api.post(
        endpoints.ubt.costCenter.createUBTTeam,
        payload,
        {
          signal,
          headers: getStandardHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to create UBT team"),
      );
    }
  },
);

export const fetchTeamDetails = createAsyncThunk(
  "costCenter/fetchTeamDetails",
  async (
    { teamId, nuid }: { teamId: number | string; nuid?: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await api.get(
        endpoints.ubt.teams.getTeamDetails(teamId),
        {
          signal,
          headers: {
            accept: "application/json",
            "x-nuid": nuid ?? localStorage.getItem("userId") ?? "",
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch team details"),
      );
    }
  },
);

export const fetchUBTTeam = createAsyncThunk(
  "costCenter/fetchUBTTeam",
  async (
    { teamId, nuid }: { teamId: number | string; nuid?: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await api.get(endpoints.ubt.teams.getUBTTeam(teamId), {
        signal,
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "x-nuid": nuid ?? localStorage.getItem("userId") ?? "",
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch UBT team"),
      );
    }
  },
);

export const updateUBTTeam = createAsyncThunk(
  "costCenter/updateUBTTeam",
  async (
    { teamId, payload }: { teamId: number | string; payload: any },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await api.patch(
        endpoints.ubt.costCenter.updateUBTTeam(teamId),
        payload,
        {
          signal,
          headers: getStandardHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to update UBT team"),
      );
    }
  },
);

export const addCostCenterAPI = createAsyncThunk(
  "costCenter/addCostCenterAPI",
  async (
    { teamId, payload }: { teamId: number | string; payload: any },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await api.put(
        endpoints.ubt.costCenter.addCostCenter(teamId),
        payload,
        {
          signal,
          headers: getStandardHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to add cost center"),
      );
    }
  },
);

export const updateCostCenterAPI = createAsyncThunk(
  "costCenter/updateCostCenterAPI",
  async (
    { teamId, payload }: { teamId: number | string; payload: any },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await api.patch(
        endpoints.ubt.costCenter.updateCostCenter(teamId),
        payload,
        {
          signal,
          headers: getStandardHeaders(),
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to update cost center"),
      );
    }
  },
);

export const deleteCostCenterAPI = createAsyncThunk(
  "costCenter/deleteCostCenterAPI",
  async (
    {
      teamId,
      payload,
      costCenterId,
    }: { teamId: number | string; payload: any; costCenterId?: number },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await api.post(
        endpoints.ubt.costCenter.deleteCostCenter(teamId),
        payload,
        {
          signal,
          headers: getStandardHeaders(),
        },
      );
      return { data: response.data, costCenterId };
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to delete cost center"),
      );
    }
  },
);

// Fetch Entity options based on region (returned with AREA call)
export const fetchEntityOptions = createAsyncThunk(
  "selectUBT/fetchEntityOptions",
  async (regionCode: string, { rejectWithValue }) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "AREA",
        regionCode,
      });

      const entityOptions = addDefaultOption(
        transformToDropdownOptions(response?.entityList ?? [], false),
        defaultOption,
      );
      return entityOptions;
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch entity options"),
      );
    }
  },
);


// Fetch Area options based on region
export const fetchAreaOptions = createAsyncThunk(
  "selectUBT/fetchAreaOptions",
  async (
    {
      regionCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: { regionCode: string; sortBy?: string; isRestored?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "AREA",
        regionCode,
        sortBy: (sortBy === "name" ? "DEPTNAME" : "DEPTID"),
      });

      const areaOptions = addDefaultOption(
        transformToDropdownOptions(response?.selectedList ?? []),
        defaultOption,
      );

      const entityOptions = addDefaultOption(
        transformToDropdownOptions(response?.entityList ?? []),
        defaultOption,
      );

      return { areaOptions, entityOptions, isRestored };
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch area options"),
      );
    }
  },
);

// Fetch Center options based on area
export const fetchCenterOptions = createAsyncThunk(
  "selectUBT/fetchCenterOptions",
  async (
    {
      regionCode,
      areaCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "CENTER",
        regionCode,
        areaCode,
        sortBy: (sortBy === "name" ? "DEPTNAME" : "DEPTID"),
      });

      const centerOptions = addDefaultOption(
        transformToDropdownOptions(response?.selectedList ?? []),
        defaultOption,
      );

      return { centerOptions, isRestored };
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch center options"),
      );
    }
  },
);

// Fetch Location options based on center
export const fetchLocationOptions = createAsyncThunk(
  "selectUBT/fetchLocationOptions",
  async (
    {
      regionCode,
      areaCode,
      centerCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      centerCode: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "LOCATION",
        regionCode,
        areaCode,
        serviceAreaCode: centerCode,
        sortBy: (sortBy === "name" ? "DEPTNAME" : "DEPTID"),
      });
      let locationOptions: DropdownOption[] = [];
      if (centerCode !== defaultOption.value) {
        locationOptions = addDefaultOption(
          transformToDropdownOptions(response?.selectedList ?? []),
          defaultOption,
        );
      }

      return { locationOptions, isRestored };
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch location options"),
      );
    }
  },
);

// Fetch Cost Center options based on center and sort criteria
export const fetchCostCenterOptions = createAsyncThunk(
  "selectUBT/fetchCostCenterOptions",
  async (
    {
      regionCode,
      areaCode,
      centerCode,
      locationCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      centerCode: string;
      locationCode?: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "COST_CENTER",
        regionCode,
        areaCode,
        serviceAreaCode: centerCode,
        locationCode: locationCode ?? "ALL",
        sortBy: (sortBy === "name" ? "DEPTNAME" : "DEPTID"),
      });

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { costCenterOptions, isRestored };
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch cost center options"),
      );
    }
  },
);

// Fetch Cost Center dropdown options from the add-edit endpoint.
// This is a new separate thunk and does NOT replace the existing fetchCostCenterOptions.
export const fetchAddEditCostCenterOptions = createAsyncThunk(
  "costCenter/fetchAddEditCostCenterOptions",
  async (
    {
      regionCd,
      areaCd,
      locationCd,
      entityCd,
      centerCd,
    }: {
      regionCd: string;
      areaCd: string;
      locationCd: string;
      entityCd: string;
      centerCd: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(
        endpoints.ubt.costCenter.getCostCenterDropdowns({
          regionCd,
          areaCd,
          locationCd,
          entityCd,
          centerCd,
        }),
        { headers: getStandardHeaders(false) },
      );
      const items: { deptId: string; deptName: string }[] =
        response?.data?.costCenters ?? [];
      const costCenterOptions: DropdownOption[] = addDefaultOption(
        items.map((item, index) => ({
          id: index + 1,
          label: `${item.deptId} ${item.deptName}`,
          value: item.deptId,
        })),
        defaultOption,
      );
      return { costCenterOptions };
    } catch (error: any) {
      return rejectWithValue(
        handleThunkError(error, "Failed to fetch cost center dropdown options"),
      );
    }
  },
);

const costCenterSlice = createSlice({
  name: "costCenter",
  initialState,
  reducers: {
    resetCostCenterState: (state) => {
      state.costCenters = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    // Team profile actions
    setTeamId: (state, action: PayloadAction<string>) => {
      state.teamId = action.payload;
    },
    setUbtName: (state, action: PayloadAction<string>) => {
      state.ubtName = action.payload;
    },
    setDateTeamEstablished: (state, action: PayloadAction<string>) => {
      state.dateTeamEstablished = action.payload;
    },
    setTeamRating: (state, action: PayloadAction<string>) => {
      state.teamRating = action.payload;
    },
    setServiceType: (state, action: PayloadAction<string>) => {
      state.serviceType = action.payload;
    },
    setUsingHuddles: (state, action: PayloadAction<string>) => {
      state.usingHuddles = action.payload;
    },
    setDefaultMembership: (state, action: PayloadAction<string>) => {
      state.defaultMembership = action.payload;
    },
    // Cost center list actions
    setCostCenterList: (state, action: PayloadAction<CostCenter[]>) => {
      state.costCenterList = action.payload;
    },
    addCostCenter: (state, action: PayloadAction<CostCenter>) => {
      state.costCenterList.push(action.payload);
    },
    updateCostCenter: (state, action: PayloadAction<CostCenter>) => {
      const index = state.costCenterList.findIndex(
        (cc) => cc.costCenterId === action.payload.costCenterId,
      );
      if (index !== -1) {
        state.costCenterList[index] = action.payload;
      }
    },
    removeCostCenter: (state, action: PayloadAction<number>) => {
      state.costCenterList = state.costCenterList.filter(
        (cc) => cc.costCenterId !== action.payload,
      );
    },
    // UI state actions
    setEditableTeam: (state, action: PayloadAction<boolean>) => {
      state.editableTeam = action.payload;
    },
    setEditingTeam: (state, action: PayloadAction<boolean>) => {
      state.editingTeam = action.payload;
    },
    setCreateTeam: (state, action: PayloadAction<boolean>) => {
      state.createTeam = action.payload;
    },
    // Reset team profile to initial values
    resetTeamProfile: (state) => {
      state.teamId = "";
      state.ubtName = "";
      state.dateTeamEstablished = formatTimestampMDY(new Date());
      state.teamRating = "1 Pre-Team Climate";
      state.serviceType = "Call Center";
      state.usingHuddles = "no";
      state.defaultMembership = "yes";
      state.costCenterList = [];
      state.editableTeam = false;
      state.editingTeam = false;
      state.createTeam = true;
    },
    // Reset dropdown options when parent fields are changed to "Select"
    resetCenterOptions: (state) => {
      state.centerOptions = [defaultOption];
    },
    resetLocationOptions: (state) => {
      state.locationOptions = [defaultOption];
    },
    resetCostCenterOptions: (state) => {
      state.costCenterOptions = [defaultOption];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCostCenterUBTList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCostCenterUBTList.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      // Handle API response with meta object and items array (prioritize this format)
      if (payload?.meta && payload?.items && Array.isArray(payload.items)) {
        console.log("Processing meta + items format. Items:", payload.items);
        state.costCenters = payload.items;
        state.totalItems = payload.meta.total ?? 0;
        state.totalPages = payload.meta.totalPages ?? 1;
        state.currentPage = payload.meta.pageNumber ?? 1;
        state.perPage = payload.meta.pageSize ?? 10;
      } else {
        state.costCenters = payload ? [payload] : [];
      }
    });
    builder.addCase(fetchCostCenterUBTList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // Handle fetchTeamDetails
    builder.addCase(fetchTeamDetails.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTeamDetails.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      state.teamDetails = payload ?? null;

      // Handle array response where each item contains both team and cost center data
      if (Array.isArray(payload) && payload.length > 0) {
        // Extract team details from first item and map to state
        const teamData = mapTeamDetailsToState(payload[0]);
        Object.assign(state, teamData);

        // Map entire array to cost centers
        const mappedCenters = payload.map(mapCostCenterItem);
        console.log("Mapped cost centers:", mappedCenters);
        state.costCenters = mappedCenters as any;
        state.costCenterList = mappedCenters;
      } else {
        // Handle single object response
        const teamData = mapTeamDetailsToState(payload ?? {});
        Object.assign(state, teamData);
      }

      // Set UI state for viewing existing team
      state.editableTeam = true;
      state.editingTeam = false;
      state.createTeam = false;
    });
    builder.addCase(fetchTeamDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Handle fetchUBTTeam
    builder.addCase(fetchUBTTeam.pending, (state) => {
      state.loading = true;
      state.error = null;
      // Clear stale cost centers from the previous team so they are not
      // briefly displayed while the new team's data is being fetched.
      state.costCenterList = [];
      state.costCenters = [] as any;
    });
    builder.addCase(fetchUBTTeam.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      state.teamDetails = payload ?? null;

      if (payload) {
        // Extract team-level details from root object
        state.teamId = String(payload.teamId ?? "");
        state.ubtName = payload.teamName?.trim() ?? "";
        state.dateTeamEstablished = formatDateOnlyMDY(payload.teamDateCreated);

        // Combine likertScaleRating and likertRatingDescription for teamRating
        const rating = payload.likertScaleRating;
        const ratingDesc = payload.likertRatingDescription;
        state.teamRating =
          rating && ratingDesc ? `${rating} ${ratingDesc}` : state.teamRating;

        state.serviceType = payload.serviceDeliveryType ?? state.serviceType;

        // Convert Y/N to yes/no for form display
        state.usingHuddles = payload.usingHuddles === "Y" ? "yes" : "no";

        // Get defaultMembership from first cost center if available
        const firstCostCenter = payload.costCenters?.[0];
        state.defaultMembership =
          firstCostCenter?.defaultMembership === "Y" ? "yes" : "no";

        // Map cost centers array with UBT-specific field mappings
        if (
          Array.isArray(payload.costCenters) &&
          payload.costCenters.length > 0
        ) {
          const mappedCenters: CostCenter[] = payload.costCenters.map(
            (item: any) => {
              // Construct composite fields for UBT team format
              const glString = `${item.company ?? ""}-${item.glLocCd ?? ""}-${item.deptId ?? ""}`;
              const entity = `${item.company ?? ""}-${item.kpCompanyDescr ?? ""}`;
              const location = `${item.glLocCd ?? ""}-${item.glLocNm ?? ""}`;
              const costCenter = `${item.deptId ?? ""}-${item.deptName ?? ""}`;

              return mapCostCenterItem({
                ...item,
                glString,
                entity,
                location,
                costCenter,
                costCenterId: item.deptPk,
                region: item.kpGlRegionCodeDescr,
                regionCode: item.kpGlRegionCd,
                entityCode: item.company,
                area: item.kpAreaDesc,
                areaCode: item.kpAreaCd,
                center: item.kpSvcAreaDesc,
                centerCode: item.kpSvcArea,
                locationCode: item.glLocCd,
              });
            },
          );

          console.log("Mapped cost centers from fetchUBTTeam:", mappedCenters);
          state.costCenters = mappedCenters as any;
          state.costCenterList = mappedCenters;
        } else {
          state.costCenterList = [];
        }
      }

      // Set UI state for viewing existing team
      state.editableTeam = true;
      state.editingTeam = false;
      state.createTeam = false;
    });
    builder.addCase(fetchUBTTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Handle fetchTeamRatingOptions
    builder.addCase(fetchTeamRatingOptions.pending, (state) => {
      state.loadingTeamRatingOptions = true;
      state.error = null;
    });
    builder.addCase(fetchTeamRatingOptions.fulfilled, (state, action) => {
      state.loadingTeamRatingOptions = false;
      // API returns { data: [...], status: 200 }, extract the data array
      state.teamRatingOptions = action.payload?.data ?? action.payload ?? [];
    });
    builder.addCase(fetchTeamRatingOptions.rejected, (state, action) => {
      state.loadingTeamRatingOptions = false;
      state.error = action.payload as string;
    });

    // Handle fetchServiceTypeOptions
    builder.addCase(fetchServiceTypeOptions.pending, (state) => {
      state.loadingServiceTypeOptions = true;
      state.error = null;
    });
    builder.addCase(fetchServiceTypeOptions.fulfilled, (state, action) => {
      state.loadingServiceTypeOptions = false;
      // API returns { data: [...], status: 200 }, extract the data array
      state.serviceTypeOptions = action.payload?.data ?? action.payload ?? [];
    });
    builder.addCase(fetchServiceTypeOptions.rejected, (state, action) => {
      state.loadingServiceTypeOptions = false;
      state.error = action.payload as string;
    });

    // Handle fetchShiftOptions
    builder.addCase(fetchShiftOptions.pending, (state) => {
      state.loadingShiftOptions = true;
      state.error = null;
    });
    builder.addCase(fetchShiftOptions.fulfilled, (state, action) => {
      state.loadingShiftOptions = false;
      state.shiftOptions = action.payload ?? [];
    });
    builder.addCase(fetchShiftOptions.rejected, (state, action) => {
      state.loadingShiftOptions = false;
      state.error = action.payload as string;
    });

    // Handle createUBTTeam
    builder.addCase(createUBTTeam.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createUBTTeam.fulfilled, (state, action) => {
      state.loading = false;
      const response: any = action.payload;

      // Update team info with created team details
      if (response?.teamDetails) {
        const teamDetails = response.teamDetails;
        state.teamId = String(teamDetails.teamId ?? "");
        state.ubtName = teamDetails.teamName?.trim() ?? "";
        state.dateTeamEstablished = formatTimestampMDY(
          teamDetails.teamDateEstablished,
        );
        state.teamRating = teamDetails.rating
          ? `${teamDetails.rating} ${state.teamRatingOptions.find((opt: any) => opt.rating === teamDetails.rating)?.text ?? ""}`
          : state.teamRating;
        state.serviceType =
          teamDetails.serviceDeliveryType ?? state.serviceType;
        state.usingHuddles = teamDetails.usingHuddles === "Y" ? "yes" : "no";
        state.defaultMembership =
          teamDetails.defaultMembership === "Y" ? "yes" : "no";
      }

      // Set UI state to viewing mode
      state.editableTeam = true;
      state.editingTeam = false;
      state.createTeam = false;
    });
    builder.addCase(createUBTTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Handle updateUBTTeam
    builder.addCase(updateUBTTeam.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUBTTeam.fulfilled, (state, action) => {
      state.loading = false;
      const response: any = action.payload;

      // Update team info with updated team details
      if (response?.teamDetails) {
        const teamDetails = response.teamDetails;
        state.teamId = String(teamDetails.teamId ?? state.teamId);
        state.ubtName = teamDetails.teamName?.trim() ?? state.ubtName?.trim();
        state.dateTeamEstablished = teamDetails.teamDateCreated
          ? formatDateOnlyMDY(teamDetails.teamDateCreated)
          : state.dateTeamEstablished;
        state.teamRating = teamDetails.rating
          ? `${teamDetails.rating} ${state.teamRatingOptions.find((opt: any) => opt.rating === teamDetails.rating)?.text ?? ""}`
          : state.teamRating;
        state.serviceType =
          teamDetails.serviceDeliveryType ?? state.serviceType;
        state.usingHuddles = teamDetails.usingHuddles === "Y" ? "yes" : "no";
        state.defaultMembership =
          teamDetails.defaultMembership === "Y" ? "yes" : "no";
      }

      // Set UI state back to viewing mode
      state.editableTeam = true;
      state.editingTeam = false;
    });
    builder.addCase(updateUBTTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Add Cost Center handlers
    builder.addCase(addCostCenterAPI.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addCostCenterAPI.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(addCostCenterAPI.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // Update Cost Center handlers
    builder.addCase(updateCostCenterAPI.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateCostCenterAPI.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateCostCenterAPI.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // Delete Cost Center handlers
    builder.addCase(deleteCostCenterAPI.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteCostCenterAPI.fulfilled, (state, action) => {
      state.loading = false;
      // Attempt to remove cost center using provided id from the thunk arg or response
      const removedId =
        (action.payload &&
          (action.payload.costCenterId ??
            action.payload.data?.costCenterId ??
            action.payload.data?.id)) ??
        null;
      if (removedId !== null && removedId !== undefined) {
        state.costCenterList = state.costCenterList.filter(
          (cc) => cc.costCenterId !== removedId,
        );
        state.costCenters = (state.costCenters as any[]).filter(
          (cc: any) => cc.costCenterId !== removedId,
        );
      }
    });
    builder.addCase(deleteCostCenterAPI.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Entity Options
    builder.addCase(fetchEntityOptions.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchEntityOptions.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.entityOptions = action.payload ?? [];
    });
    builder.addCase(fetchEntityOptions.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    // Area Options
    builder.addCase(fetchAreaOptions.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchAreaOptions.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      const { areaOptions, entityOptions } = action.payload ?? {
        areaOptions: [],
        entityOptions: [],
      };
      state.areaOptions = areaOptions;
      state.entityOptions = entityOptions;
    });
    builder.addCase(fetchAreaOptions.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    // Center Options
    builder.addCase(fetchCenterOptions.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchCenterOptions.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.centerOptions = action.payload?.centerOptions ?? [];
    });
    builder.addCase(fetchCenterOptions.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    // Location Options
    builder.addCase(fetchLocationOptions.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchLocationOptions.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.locationOptions = action.payload?.locationOptions ?? [];
    });
    builder.addCase(fetchLocationOptions.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    // Cost Center Options
    builder.addCase(fetchCostCenterOptions.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchCostCenterOptions.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.costCenterOptions = action.payload?.costCenterOptions ?? [];
    });
    builder.addCase(fetchCostCenterOptions.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    // Add-Edit Cost Center Options (new endpoint)
    builder.addCase(fetchAddEditCostCenterOptions.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchAddEditCostCenterOptions.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.costCenterOptions = action.payload?.costCenterOptions ?? [];
    });
    builder.addCase(fetchAddEditCostCenterOptions.rejected, (state) => {
      state.loadingDropdowns = false;
    });
  },
});

export const {
  resetCostCenterState,
  setCurrentPage,
  setTeamId,
  setUbtName,
  setDateTeamEstablished,
  setTeamRating,
  setServiceType,
  setUsingHuddles,
  setDefaultMembership,
  setCostCenterList,
  addCostCenter,
  updateCostCenter,
  removeCostCenter,
  setEditableTeam,
  setEditingTeam,
  setCreateTeam,
  resetTeamProfile,
  resetCenterOptions,
  resetLocationOptions,
  resetCostCenterOptions,
} = costCenterSlice.actions;

export default costCenterSlice.reducer;
